import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, User, Shield, ChevronLeft } from 'lucide-react';

const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const [logoClicks, setLogoClicks] = useState(0);
  const [showSecretMessage, setShowSecretMessage] = useState(false);

  // Handle secret owner access (5 taps on logo)
  const handleLogoClick = () => {
    const newClicks = logoClicks + 1;
    setLogoClicks(newClicks);
    
    if (newClicks >= 5) {
      setShowSecretMessage(true);
      setTimeout(() => {
        navigate('/owner/login');
      }, 500);
    } else {
      setTimeout(() => {
        setLogoClicks(0);
        setShowSecretMessage(false);
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700 flex flex-col items-center justify-center p-6">
      {/* Logo Section */}
      <div 
        className="mb-8 cursor-pointer select-none"
        onClick={handleLogoClick}
      >
        <div className="w-32 h-32 bg-white rounded-3xl shadow-2xl flex items-center justify-center mb-4 mx-auto transform transition-transform hover:scale-105">
          <Car className="w-16 h-16 text-primary-500" />
        </div>
        <h1 className="text-4xl font-bold text-white text-center">رحلة</h1>
        <p className="text-primary-100 text-center mt-2">Rahla - Your Journey, Our Priority</p>
        
        {logoClicks > 0 && logoClicks < 5 && (
          <p className="text-white/70 text-center text-sm mt-2">
            {5 - logoClicks} taps remaining
          </p>
        )}
        
        {showSecretMessage && (
          <p className="text-white text-center text-sm mt-2 animate-pulse">
            Owner access detected...
          </p>
        )}
      </div>

      {/* Welcome Text */}
      <div className="text-center mb-10">
        <h2 className="text-2xl font-semibold text-white mb-2">مرحباً بك في رحلة</h2>
        <p className="text-primary-100">اختر نوع حسابك للمتابعة</p>
      </div>

      {/* Role Selection Cards */}
      <div className="w-full max-w-md space-y-4">
        {/* Passenger Card */}
        <button
          onClick={() => navigate('/passenger/login')}
          className="w-full bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-4 group"
        >
          <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center group-hover:bg-primary-500 transition-colors">
            <User className="w-7 h-7 text-primary-500 group-hover:text-white transition-colors" />
          </div>
          <div className="flex-1 text-right">
            <h3 className="text-lg font-semibold text-gray-800">أنا راكب</h3>
            <p className="text-gray-500 text-sm">اطلب رحلة وانتقل بسهولة</p>
          </div>
          <ChevronLeft className="w-5 h-5 text-gray-400 group-hover:text-primary-500 transition-colors" />
        </button>

        {/* Driver Card */}
        <button
          onClick={() => navigate('/driver/login')}
          className="w-full bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-4 group"
        >
          <div className="w-14 h-14 bg-secondary-100 rounded-xl flex items-center justify-center group-hover:bg-secondary-500 transition-colors">
            <Car className="w-7 h-7 text-secondary-600 group-hover:text-white transition-colors" />
          </div>
          <div className="flex-1 text-right">
            <h3 className="text-lg font-semibold text-gray-800">أنا سائق</h3>
            <p className="text-gray-500 text-sm">اكسب المال مع رحلة</p>
          </div>
          <ChevronLeft className="w-5 h-5 text-gray-400 group-hover:text-secondary-500 transition-colors" />
        </button>
      </div>

      {/* Footer */}
      <div className="mt-10 text-center">
        <p className="text-primary-200 text-sm">
          بتسجيل الدخول، أنت توافق على{' '}
          <a href="#" className="text-white underline hover:no-underline">شروط الاستخدام</a>
          {' '}و{' '}
          <a href="#" className="text-white underline hover:no-underline">سياسة الخصوصية</a>
        </p>
        <p className="text-primary-300 text-xs mt-4">
          © 2024 رحلة - جميع الحقوق محفوظة
        </p>
      </div>

      {/* Hidden Owner Hint */}
      <div className="absolute bottom-4 left-4 opacity-0 hover:opacity-30 transition-opacity">
        <Shield className="w-4 h-4 text-white/30" />
      </div>
    </div>
  );
};

export default WelcomePage;
